import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, inject } from '@angular/core';

/**
 * Mantém o foco do teclado dentro do elemento host enquanto ele existe (diálogos,
 * drawers). Ao iniciar, move o foco para o primeiro controle focável (ou o
 * próprio host); ao destruir, devolve o foco a quem o disparou. Cicla Tab /
 * Shift+Tab nas bordas. Substitui o `cdkTrapFocus` (sem depender do `@angular/cdk`).
 */
@Directive({ selector: '[ubFocusTrap]' })
export class FocusTrap implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private restoreTo: HTMLElement | null = null;

  ngAfterViewInit(): void {
    this.restoreTo = (document.activeElement as HTMLElement) ?? null;
    queueMicrotask(() => this.focusFirst());
  }

  ngOnDestroy(): void {
    this.restoreTo?.focus?.();
  }

  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }
    const items = this.focusable();
    if (items.length === 0) {
      event.preventDefault();
      this.host.nativeElement.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.host.nativeElement)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusFirst(): void {
    const preferred = this.host.nativeElement.querySelector<HTMLElement>('[autofocus]');
    if (preferred) {
      preferred.focus();
      return;
    }
    const items = this.focusable();
    (items[0] ?? this.host.nativeElement).focus?.();
  }

  private focusable(): HTMLElement[] {
    const selector =
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement,
    );
  }
}
